import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Check, Download, Edit3, FileText, Loader2, Plus, Search, Star, Trash2, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const formularioVazio = {
  titulo: '',
  conteudo: '',
  tags: '',
  favorito: false
}

const formatarAtualizacao = anotacao => {
  const valor = anotacao.updatedAt || anotacao.updated_at || anotacao.createdAt
  if (!valor) return ''

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(valor))
}

const formatarTamanho = valor => {
  const bytes = Number(valor || 0)
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function CadernoPage() {
  const queryClient = useQueryClient()
  const { usuario, accessToken } = useAuthStore()
  const [aba, setAba] = useState('todas')
  const [busca, setBusca] = useState('')
  const [formularioAberto, setFormularioAberto] = useState(false)
  const [anotacaoEmEdicao, setAnotacaoEmEdicao] = useState(null)
  const [confirmarExclusao, setConfirmarExclusao] = useState(null)
  const [confirmarExclusaoArquivo, setConfirmarExclusaoArquivo] = useState(null)
  const [arquivosSelecionados, setArquivosSelecionados] = useState([])
  const [baixandoArquivo, setBaixandoArquivo] = useState(null)
  const [formulario, setFormulario] = useState(formularioVazio)

  const {
    data: anotacoes = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['caderno'],
    queryFn: async () => (await api.get('/caderno')).data.anotacoes,
    enabled: Boolean(accessToken)
  })

  const anotacoesFiltradas = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR')

    return anotacoes.filter(anotacao => {
      if (aba === 'favoritos' && !anotacao.favorito) return false
      if (!termo) return true

      return anotacao.titulo.toLocaleLowerCase('pt-BR').includes(termo) ||
        anotacao.conteudo.toLocaleLowerCase('pt-BR').includes(termo) ||
        (anotacao.tags || []).some(tag => tag.toLocaleLowerCase('pt-BR').includes(termo)) ||
        (anotacao.arquivos || []).some(arquivo => arquivo.nome_original.toLocaleLowerCase('pt-BR').includes(termo))
    })
  }, [aba, anotacoes, busca])

  const fecharFormulario = () => {
    setFormularioAberto(false)
    setAnotacaoEmEdicao(null)
    setArquivosSelecionados([])
    setFormulario(formularioVazio)
  }

  const abrirNovaAnotacao = () => {
    setAnotacaoEmEdicao(null)
    setArquivosSelecionados([])
    setFormulario(formularioVazio)
    setFormularioAberto(true)
  }

  const editarAnotacao = anotacao => {
    setAnotacaoEmEdicao(anotacao.id)
    setArquivosSelecionados([])
    setFormulario({
      titulo: anotacao.titulo,
      conteudo: anotacao.conteudo,
      tags: (anotacao.tags || []).join(', '),
      favorito: anotacao.favorito
    })
    setFormularioAberto(true)
  }

  const salvarAnotacao = useMutation({
    mutationFn: async () => {
      const payload = {
        titulo: formulario.titulo.trim(),
        conteudo: formulario.conteudo.trim(),
        tags: [...new Set(formulario.tags.split(',').map(tag => tag.trim()).filter(Boolean))],
        favorito: formulario.favorito
      }

      const response = anotacaoEmEdicao
        ? api.put(`/caderno/${anotacaoEmEdicao}`, payload)
        : api.post('/caderno', payload)
      const { data } = await response
      const anotacaoId = data.anotacao.id
      const falhas = []

      for (const arquivo of arquivosSelecionados) {
        const formData = new FormData()
        formData.append('arquivo', arquivo)
        try {
          await api.post(`/caderno/${anotacaoId}/arquivos`, formData)
        } catch (error) {
          falhas.push(getApiError(error, `Não foi possível anexar ${arquivo.name}.`))
        }
      }

      return { editando: Boolean(anotacaoEmEdicao), falhas }
    },
    onSuccess: ({ editando, falhas }) => {
      if (falhas.length) {
        toast.error(`Anotação salva, mas ${falhas.length} PDF(s) não foram anexados.`)
      } else {
        toast.success(editando ? 'Anotação atualizada!' : 'Anotação criada!')
      }
      queryClient.invalidateQueries({ queryKey: ['caderno'] })
      fecharFormulario()
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível salvar a anotação.'))
  })

  const alternarFavorito = useMutation({
    mutationFn: anotacao => api.put(`/caderno/${anotacao.id}`, { favorito: !anotacao.favorito }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['caderno'] }),
    onError: error => toast.error(getApiError(error, 'Não foi possível atualizar o favorito.'))
  })

  const excluirAnotacao = useMutation({
    mutationFn: id => api.delete(`/caderno/${id}`),
    onSuccess: () => {
      toast.success('Anotação excluída.')
      setConfirmarExclusao(null)
      queryClient.invalidateQueries({ queryKey: ['caderno'] })
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível excluir a anotação.'))
  })

  const excluirArquivo = useMutation({
    mutationFn: ({ anotacaoId, arquivoId }) => api.delete(`/caderno/${anotacaoId}/arquivos/${arquivoId}`),
    onSuccess: () => {
      toast.success('PDF removido.')
      setConfirmarExclusaoArquivo(null)
      queryClient.invalidateQueries({ queryKey: ['caderno'] })
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível remover o PDF.'))
  })

  const selecionarArquivos = event => {
    const novosArquivos = Array.from(event.target.files || [])
    const anotacaoAtual = anotacoes.find(anotacao => anotacao.id === anotacaoEmEdicao)
    const quantidadeExistente = anotacaoAtual?.arquivos?.length || 0
    const limiteDisponivel = 5 - quantidadeExistente - arquivosSelecionados.length

    const validos = novosArquivos.filter(arquivo => {
      if (arquivo.type !== 'application/pdf' && !arquivo.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${arquivo.name} não é um PDF.`)
        return false
      }
      if (arquivo.size > 10 * 1024 * 1024) {
        toast.error(`${arquivo.name} ultrapassa 10 MB.`)
        return false
      }
      return true
    }).slice(0, Math.max(0, limiteDisponivel))

    if (novosArquivos.length > limiteDisponivel) {
      toast.error('Cada anotação pode ter no máximo 5 PDFs.')
    }
    setArquivosSelecionados(atuais => [...atuais, ...validos])
    event.target.value = ''
  }

  const baixarPdf = async (anotacaoId, arquivo) => {
    setBaixandoArquivo(arquivo.id)
    try {
      const { data } = await api.get(`/caderno/${anotacaoId}/arquivos/${arquivo.id}`, {
        responseType: 'blob'
      })
      const url = URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = arquivo.nome_original
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      toast.error(getApiError(error, 'Não foi possível baixar o PDF.'))
    } finally {
      setBaixandoArquivo(null)
    }
  }

  const enviarFormulario = event => {
    event.preventDefault()
    salvarAnotacao.mutate()
  }

  if (!usuario) {
    return (
      <div className="card text-center py-8">
        <BookOpen size={24} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-600 mb-3">Entre para criar e consultar suas anotações.</p>
        <Link to="/login" className="btn-primary inline-block">Entrar</Link>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium lg:text-2xl">Meu Caderno</h1>
          <p className="text-xs text-gray-500">Suas anotações ficam salvas na sua conta.</p>
        </div>
        <button
          type="button"
          onClick={abrirNovaAnotacao}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-black text-white hover:bg-gray-800 sm:w-auto sm:px-3"
          aria-label="Nova anotação"
        >
          <Plus size={18} />
          <span className="hidden text-xs font-medium sm:inline">Nova anotação</span>
        </button>
      </div>

      {formularioAberto && (
        <form className="card max-w-4xl space-y-3" onSubmit={enviarFormulario}>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">
              {anotacaoEmEdicao ? 'Editar anotação' : 'Nova anotação'}
            </h2>
            <button type="button" onClick={fecharFormulario} className="p-1 text-gray-400 hover:text-black" aria-label="Fechar formulário">
              <X size={17} />
            </button>
          </div>
          <input
            className="input-field"
            value={formulario.titulo}
            onChange={event => setFormulario({ ...formulario, titulo: event.target.value })}
            placeholder="Título da anotação"
            maxLength={200}
            required
            autoFocus
          />
          <textarea
            className="input-field min-h-32 resize-y"
            value={formulario.conteudo}
            onChange={event => setFormulario({ ...formulario, conteudo: event.target.value })}
            placeholder="Escreva o que você quer lembrar..."
            required
          />
          <input
            className="input-field"
            value={formulario.tags}
            onChange={event => setFormulario({ ...formulario, tags: event.target.value })}
            placeholder="Tags separadas por vírgula (opcional)"
          />
          <div className="rounded-lg border border-dashed border-gray-200 p-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600 hover:text-black">
              <Upload size={15} /> Adicionar PDF
              <input
                type="file"
                accept="application/pdf,.pdf"
                multiple
                onChange={selecionarArquivos}
                className="sr-only"
              />
            </label>
            <p className="mt-1 text-[10px] text-gray-400">Até 5 arquivos por anotação, com no máximo 10 MB cada.</p>
            {arquivosSelecionados.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {arquivosSelecionados.map((arquivo, indice) => (
                  <div key={`${arquivo.name}-${arquivo.size}-${indice}`} className="flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5">
                    <FileText size={13} className="flex-shrink-0 text-red-500" />
                    <span className="min-w-0 flex-1 truncate text-xs text-gray-600">{arquivo.name}</span>
                    <span className="text-[10px] text-gray-400">{formatarTamanho(arquivo.size)}</span>
                    <button
                      type="button"
                      onClick={() => setArquivosSelecionados(atuais => atuais.filter((_, itemIndice) => itemIndice !== indice))}
                      className="p-1 text-gray-400 hover:text-red-500"
                      aria-label={`Remover ${arquivo.name} da seleção`}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFormulario({ ...formulario, favorito: !formulario.favorito })}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors ${
                formulario.favorito ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500 hover:text-black'
              }`}
              aria-pressed={formulario.favorito}
            >
              <Star size={14} className={formulario.favorito ? 'fill-amber-400 text-amber-400' : ''} />
              {formulario.favorito ? 'Favorita' : 'Marcar como favorita'}
            </button>
            <button type="submit" disabled={salvarAnotacao.isLoading} className="btn-primary inline-flex items-center gap-2">
              {salvarAnotacao.isLoading ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {salvarAnotacao.isLoading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={busca}
            onChange={event => setBusca(event.target.value)}
            placeholder="Buscar anotações..."
            className="input-field pl-9"
          />
        </div>

        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {[
            { key: 'todas', label: `Anotações (${anotacoes.length})` },
            { key: 'favoritos', label: `Favoritos (${anotacoes.filter(item => item.favorito).length})` }
          ].map(tab => (
            <button
              type="button"
              key={tab.key}
              onClick={() => setAba(tab.key)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                aba === tab.key ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="card flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 size={17} className="animate-spin" /> Carregando anotações...
        </div>
      )}

      {isError && (
        <div className="card text-center py-8">
          <p className="text-sm text-gray-600 mb-3">Não foi possível carregar seu caderno.</p>
          <button type="button" onClick={() => refetch()} className="btn-secondary">Tentar novamente</button>
        </div>
      )}

      {!isLoading && !isError && anotacoesFiltradas.length === 0 && (
        <div className="card text-center py-8">
          <BookOpen size={24} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium">
            {busca ? 'Nenhuma anotação encontrada' : aba === 'favoritos' ? 'Nenhuma favorita ainda' : 'Seu caderno está vazio'}
          </p>
          <p className="text-xs text-gray-500 mt-1 mb-4">
            {busca ? 'Tente buscar por outro termo.' : 'Crie sua primeira anotação para começar.'}
          </p>
          {!busca && aba === 'todas' && (
            <button type="button" onClick={abrirNovaAnotacao} className="btn-primary inline-flex items-center gap-2">
              <Plus size={15} /> Nova anotação
            </button>
          )}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {!isLoading && !isError && anotacoesFiltradas.map(anotacao => {
          const dataAtualizacao = formatarAtualizacao(anotacao)

          return (
            <div key={anotacao.id} className="h-fit rounded-xl border border-gray-100 p-3 transition-all hover:border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium">{anotacao.titulo}</h3>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {anotacao.tema?.nome || 'Anotação livre'}{dataAtualizacao && ` · ${dataAtualizacao}`}
                  </p>
                  <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">{anotacao.conteudo}</p>
                  {(anotacao.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {anotacao.tags.map(tag => (
                        <span key={tag} className="rounded-full bg-gray-50 px-2 py-0.5 text-[10px] text-gray-500">#{tag}</span>
                      ))}
                    </div>
                  )}
                  {(anotacao.arquivos || []).length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {anotacao.arquivos.map(arquivo => (
                        <div key={arquivo.id} className="rounded-lg bg-gray-50 px-2.5 py-2">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="flex-shrink-0 text-red-500" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium text-gray-700">{arquivo.nome_original}</p>
                              <p className="text-[10px] text-gray-400">PDF · {formatarTamanho(arquivo.tamanho_bytes)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => baixarPdf(anotacao.id, arquivo)}
                              disabled={baixandoArquivo === arquivo.id}
                              className="p-1.5 text-gray-400 hover:text-black disabled:opacity-50"
                              aria-label={`Baixar ${arquivo.nome_original}`}
                            >
                              {baixandoArquivo === arquivo.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmarExclusaoArquivo(arquivo.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500"
                              aria-label={`Remover ${arquivo.nome_original}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          {confirmarExclusaoArquivo === arquivo.id && (
                            <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-200 pt-2">
                              <span className="mr-auto text-[10px] text-gray-500">Remover este PDF?</span>
                              <button type="button" onClick={() => setConfirmarExclusaoArquivo(null)} className="text-[10px] text-gray-500">Cancelar</button>
                              <button
                                type="button"
                                onClick={() => excluirArquivo.mutate({ anotacaoId: anotacao.id, arquivoId: arquivo.id })}
                                disabled={excluirArquivo.isLoading}
                                className="rounded-md bg-red-100 px-2 py-1 text-[10px] font-medium text-red-600 disabled:opacity-50"
                              >
                                Remover
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-0.5">
                  <button
                    type="button"
                    onClick={() => alternarFavorito.mutate(anotacao)}
                    className="p-1.5 hover:bg-amber-50 rounded-lg"
                    aria-label={anotacao.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    disabled={alternarFavorito.isLoading}
                  >
                    <Star size={14} className={anotacao.favorito ? 'text-amber-400 fill-amber-400' : 'text-gray-400'} />
                  </button>
                  <button type="button" onClick={() => editarAnotacao(anotacao)} className="p-1.5 hover:bg-gray-100 rounded-lg" aria-label="Editar anotação">
                    <Edit3 size={14} className="text-gray-400" />
                  </button>
                  <button type="button" onClick={() => setConfirmarExclusao(anotacao.id)} className="p-1.5 hover:bg-red-50 rounded-lg" aria-label="Excluir anotação">
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>
              </div>
              {confirmarExclusao === anotacao.id && (
                <div className="mt-3 flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                  <span className="mr-auto text-xs text-gray-500">Excluir esta anotação?</span>
                  <button type="button" onClick={() => setConfirmarExclusao(null)} className="text-xs text-gray-500 hover:text-black">Cancelar</button>
                  <button
                    type="button"
                    onClick={() => excluirAnotacao.mutate(anotacao.id)}
                    disabled={excluirAnotacao.isLoading}
                    className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
                  >
                    Excluir
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default CadernoPage
